import { ApiRoutes, PUBLIC_API_PREFIX, type User } from "@engo/shared";
import {
  BookOpen,
  BookOpenText,
  ChevronDown,
  Dumbbell,
  GraduationCap,
  Languages,
  LayoutDashboard,
  Layers,
  LogOut,
  Menu,
  PenLine,
  RotateCcw,
  Settings,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { EngoLogo } from "../brand/EngoLogo.js";

const vocabularyChildren = [
  { to: "/words", labelKey: "nav.words", icon: BookOpen },
  { to: "/review", labelKey: "nav.review", icon: RotateCcw },
] as const;

const practiceChildren = [
  { to: "/practice/reading", labelKey: "nav.reading", icon: BookOpenText },
  { to: "/practice/writing", labelKey: "nav.writing", icon: PenLine },
] as const;

const nav = [
  { type: "link" as const, to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { type: "group" as const, labelKey: "nav.vocabulary", icon: Layers, children: vocabularyChildren },
  { type: "group" as const, labelKey: "nav.practice", icon: Dumbbell, children: practiceChildren },
  { type: "link" as const, to: "/grammar", labelKey: "nav.grammar", icon: GraduationCap },
  { type: "link" as const, to: "/settings", labelKey: "nav.settings", icon: Settings },
] as const;

export function AppShell({ user, children }: { user: User; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [loggingOut, setLoggingOut] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  async function logout() {
    setLoggingOut(true);
    try {
      await fetch(`${PUBLIC_API_PREFIX}${ApiRoutes.authLogout}`, {
        method: "POST",
        credentials: "include",
      });
      queryClient.removeQueries({ queryKey: ["auth", "me"] });
      navigate("/login", { replace: true });
    } catch {
      setLoggingOut(false);
    }
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? "bg-[var(--primary)]/12 text-[var(--accent)]"
        : "text-[var(--text-secondary)] hover:bg-white/5 hover:text-[var(--text-primary)]"
    }`;

  return (
    <div className="relative z-10 flex h-screen overflow-hidden">
      {/* Mobile drawer backdrop */}
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-label={t("shell.closeMenu")}
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-60 flex-col overflow-hidden border-r border-[var(--glass-border)] bg-[var(--glass-bg)] p-4 backdrop-blur-xl transition-transform md:static md:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="mb-8 flex items-center gap-2 px-1">
          <EngoLogo size={36} className="rounded-lg" />
          <span className="font-display text-lg font-semibold tracking-tight text-[var(--text-primary)]">
            Engo
          </span>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-1">
          {nav.map((item) => {
            if (item.type === "group") {
              const GroupIcon = item.icon;
              const isOnGroupRoute = item.children.some((child) =>
                location.pathname.startsWith(child.to),
              );
              const expanded = !collapsedGroups[item.labelKey];
              return (
                <div key={item.labelKey}>
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsedGroups((prev) => ({ ...prev, [item.labelKey]: !prev[item.labelKey] }))
                    }
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isOnGroupRoute
                        ? "text-[var(--accent)]"
                        : "text-[var(--text-secondary)] hover:bg-white/5 hover:text-[var(--text-primary)]"
                    }`}
                  >
                    <GroupIcon
                      className={`h-4 w-4 shrink-0 ${isOnGroupRoute ? "opacity-100" : "opacity-70"}`}
                      aria-hidden
                    />
                    <span className="flex-1 text-left">{t(item.labelKey)}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
                      aria-hidden
                    />
                  </button>
                  {expanded ? (
                    <div className="mt-1 ml-4 space-y-1 border-l border-[var(--glass-border)] pl-3">
                      {item.children.map(({ to, labelKey, icon: Icon }) => (
                        <NavLink
                          key={to}
                          to={to}
                          className={linkClass}
                          onClick={() => setMobileOpen(false)}
                        >
                          {({ isActive }) => (
                            <>
                              <Icon
                                className={`h-4 w-4 shrink-0 ${isActive ? "opacity-100" : "opacity-70"}`}
                                aria-hidden
                              />
                              {t(labelKey)}
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            }

            const { to, labelKey, icon: Icon } = item;
            return (
              <NavLink key={to} to={to} className={linkClass} onClick={() => setMobileOpen(false)} end={to === "/"}>
                {({ isActive }) => (
                  <>
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? "opacity-100" : "opacity-70"}`} aria-hidden />
                    {t(labelKey)}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-[var(--glass-border)] pt-4">
          <p className="truncate px-1 text-xs text-[var(--text-muted)]">{user.email}</p>
          <button
            type="button"
            onClick={() => void i18n.changeLanguage(i18n.language === "vi" ? "en" : "vi")}
            className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[var(--text-secondary)] transition-colors hover:bg-white/5 hover:text-[var(--text-primary)]"
          >
            <Languages className="h-4 w-4" aria-hidden />
            {i18n.language === "vi" ? "English" : "Tiếng Việt"}
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            disabled={loggingOut}
            className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[var(--text-secondary)] transition-colors hover:bg-white/5 hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {loggingOut ? t("shell.signingOut") : t("shell.signOut")}
          </button>
        </div>
      </aside>

      <div className="flex h-screen min-h-0 flex-1 flex-col md:pl-0">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[var(--glass-border)] bg-[var(--glass-bg)]/80 px-4 py-3 backdrop-blur-xl md:px-8">
          <button
            type="button"
            className="rounded-lg p-2 text-[var(--text-secondary)] hover:bg-white/5 md:hidden"
            aria-label={t("shell.openMenu")}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex flex-1 items-center justify-end gap-3" />
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-[max(24px,calc((100%-1280px)/2))] md:py-8">
          {children}
        </main>
      </div>

    </div>
  );
}
